<template>
  <div class="album" :id="'album-'+album.id">
    <router-link :to="'/album/'+album.id" class="album__card clearfix text-decoration-none">
      <ImageContainer :source="'/api/albums/'+album.id+'/thumbnail?'+thumbnailQuery" />
      <div class="album__title">{{album.name}}</div>
      <div class="album__count">{{album.imageCount}}</div>
      <div class="album__date">
        <ImageProperty icon="date" label="Aufgenommen" no-text :value="dateRange()" />
      </div>
      <div class="album__menu-container" @click.prevent>
        <BDropdown toggle-class="album__menu-toggle text-decoration-none"
                   size="lg" variant="link" no-caret end>
          <template #button-content><Icon icon="bi:list" /></template>
          <BDropdownItem @click="refreshThumbnail"><Icon icon="bi:arrow-repeat" /> &nbsp;Refresh Thumbnail</BDropdownItem>
        </BDropdown>
      </div>
      <div v-if="hasChildren()" class="album__children-toggle btn btn-lg btn-link" @click.prevent="toggleOpen">
        <Icon :icon="isOpen ? 'bi:chevron-compact-up' : 'bi:chevron-compact-down'" />
      </div>
    </router-link>
    <div class="collapse" :class="{show: isOpen}" v-if="hasChildren()">
      <Album v-for="child in album.children" :key="child.id" :album="child" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

defineOptions({ name: 'Album' })
import { BDropdown, BDropdownItem } from 'bootstrap-vue-next'
import { Icon } from '@iconify/vue'
import ImageContainer from '@/components/ImageContainer.vue'
import ImageProperty from '@/components/ImageProperty.vue'
import AlbumModel from '@/models/Album'
import { format, isSameYear, isSameMonth } from 'date-fns'
import { de } from 'date-fns/locale'

const props = defineProps<{ album: AlbumModel }>()
const isOpen = ref(false)
const thumbnailQuery = ref('')

function hasChildren() {
  return props.album.children && props.album.children.length > 0
}

function toggleOpen() {
  isOpen.value = !isOpen.value
}

function refreshThumbnail() {
  thumbnailQuery.value = 'refresh=1&_=' + (new Date()).getTime()
}

function dateRange(): string {
  const first = new Date(props.album.firstShot)
  const last = new Date(props.album.lastShot)
  if (props.album.firstShot === props.album.lastShot) return format(first, 'd. MMMM yyyy', { locale: de })
  if (!isSameYear(first, last)) return format(first, 'dd.MM.yyyy') + ' - ' + format(last, 'dd.MM.yyyy')
  if (!isSameMonth(first, last)) return format(first, 'dd.MM.') + ' - ' + format(last, 'dd.MM.yyyy')
  return format(first, 'dd.') + ' - ' + format(last, 'd. MMMM yyyy', { locale: de })
}
</script>

<style lang="scss">
@import '../main.scss';

.album {
  padding: 4px;
  width: 100%;

  .album__card {
    display: block;
    height: 128px;
    border-radius: 10px;
    background: $card-bg;
    position: relative;
    padding: 12px calc(64px + 12px) 12px calc(128px + 12px);

    img, .image-container {
      position: absolute;
      left: 0;
      top: 0;
      display: block;
      height: 128px;
      width: 128px;
      border-radius: 10px 0 0 10px;
    }
  }

  .album__title {
    font-size: 13pt;
    height: calc(128px - 50px);
    line-height: 1.2;
    overflow: hidden;
    color: #fff;
  }

  .album__date {
    color: #777;
  }

  .album__count {
    position: absolute;
    width: 128px;
    height: 128px;
    line-height: 128px;
    left: 0;
    top: 0;
    text-align: center;
    font-weight: bold;
    color: #fff;
    -webkit-text-stroke-width: 1px;
    -webkit-text-stroke-color: rgba(0,0,0,0.33);
    text-stroke-width: 1px;
    text-stroke-color: rgba(0,0,0,0.33);
  }

  .album__menu-container {
    position: absolute;
    top: 0;
    right: 0;
  }

  .album__menu-toggle, .album__children-toggle {
    padding: 0;
    width: 64px;
    height: 64px;
    position: absolute;
    right: 0;
    text-align: center;
    line-height: 64px;
  }

  .album__children-toggle {
    bottom: 0;
  }

  .album__menu-toggle {
    top: 0;
  }

  .collapse {
    padding: 4px 0 0 4px;

    .album {
      padding-right: 0;

      &:last-child {
        padding-bottom: 0;
      }
    }
  }

  @include media-breakpoint-up(sm) {
    .album__title {
      font-size: 14pt;
    }
  }

  @include media-breakpoint-up(md) {
    padding: 8px;

    .album__title {
      font-size: 20pt;
      font-weight: lighter;
    }

    .collapse {
      padding: 8px 0 0 16px;

      .album {
        width: 100%;
      }
    }
  }

  @include media-breakpoint-up(lg) {
    width: 50%;
  }

  @include media-breakpoint-up(xxl) {
    width: 33.33%;
  }
}
</style>
